import { useEffect, useState } from "react";
import {
  Sparkle,
  Lightning,
  Printer,
  CheckCircle,
  CaretDown,
  ArrowRight,
  DownloadSimple,
  ShoppingBag,
  PencilSimple,
  ClipboardText,
  FileText,
  BookOpen,
  Chats,
  Package,
} from "@phosphor-icons/react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import RubricGenerator from "@/components/RubricGenerator";
import { FREE_DOWNLOAD_FILES, productUrl } from "@/lib/siteConfig";

const FREE_DOWNLOADS = [
  {
    id: "rubric-template",
    title: "Free Rubric Template",
    description:
      "A blank, classroom-ready rubric you can edit in Google Docs or Word.",
    icon: <FileText size={28} weight="duotone" />,
    bg: "#E8F0E6",
    file: FREE_DOWNLOAD_FILES["rubric-template"],
  },
  {
    id: "reflection-sheet",
    title: "Student Reflection Sheet",
    description:
      "A one-page worksheet that helps students self-assess after an assignment.",
    icon: <PencilSimple size={28} weight="duotone" />,
    bg: "#FFF4E8",
    file: FREE_DOWNLOAD_FILES["reflection-sheet"],
  },
  {
    id: "grading-checklist",
    title: "Project Grading Checklist",
    description:
      "A simple checklist to keep grading consistent across student projects.",
    icon: <ClipboardText size={28} weight="duotone" />,
    bg: "#F1E8F5",
    file: FREE_DOWNLOAD_FILES["grading-checklist"],
  },
];

const PAID_PRODUCTS = [
  {
    id: "editable-rubric-bundle",
    title: "Editable Rubric Bundle",
    description:
      "30+ ready-to-edit rubric templates covering writing, math, science, and more.",
    price: "$9",
    icon: <Package size={26} weight="duotone" />,
    bg: "#E8F0E6",
  },
  {
    id: "classroom-grading-pack",
    title: "Classroom Grading Pack",
    description:
      "A complete grading toolkit: rubrics, score sheets, and feedback templates.",
    price: "$12",
    icon: <ClipboardText size={26} weight="duotone" />,
    bg: "#FFF4E8",
  },
  {
    id: "project-assessment-templates",
    title: "Project Assessment Templates",
    description:
      "Printable project planning + assessment templates for K-12 classrooms.",
    price: "$8",
    icon: <FileText size={26} weight="duotone" />,
    bg: "#F1E8F5",
  },
  {
    id: "writing-rubric-pack",
    title: "Writing Rubric Pack",
    description:
      "Genre-specific writing rubrics for essays, narratives, and research papers.",
    price: "$7",
    icon: <BookOpen size={26} weight="duotone" />,
    bg: "#E6EFF5",
  },
  {
    id: "feedback-sheets",
    title: "Student Reflection & Feedback Sheets",
    description:
      "Beautiful, printable feedback templates students actually enjoy filling out.",
    price: "$6",
    icon: <Chats size={26} weight="duotone" />,
    bg: "#FCE8E8",
  },
];

const FAQS = [
  {
    q: "Is this rubric generator free?",
    a: "Yes, the basic rubric generator is free to use. No account or sign-up required.",
  },
  {
    q: "Does this tool use official state education standards?",
    a: "This tool creates general classroom rubrics based on state, subject, and grade level selection. It is not an official state standards database.",
  },
  {
    q: "Can I print the rubric?",
    a: "Yes. Click the Print Rubric button to print a clean, classroom-friendly version directly from your browser.",
  },
  {
    q: "Can I copy the rubric?",
    a: "Yes. Click Copy Rubric to copy the rubric as plain text — paste it straight into Google Docs, Word, or an email.",
  },
];

export default function Home() {
  // Smooth-scroll anchor support for #tool, #downloads, etc.
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <main className="flex-1">
        <Hero />
        <ToolSection />
        <FreeDownloadsSection />
        <PaidProductsSection />
        <SeoSection />
        <FaqSection />
      </main>
      <Footer />
    </div>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-[#FAFAF9] border-b border-[#E5E7EB]">
      <div
        aria-hidden
        className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#FFE6D0] opacity-50 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute bottom-0 -left-20 w-96 h-96 rounded-full bg-[#E6EFE4] opacity-60 blur-3xl pointer-events-none"
      />
      <div className="relative max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
        <div className="inline-flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-full px-4 py-1.5 shadow-sm mb-6">
          <Sparkle size={14} weight="fill" className="text-[#A8B5A2]" />
          <span className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]">
            100% Free · No sign-up
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5"
        >
          Free Rubric Generator
          <br className="hidden sm:block" /> for Teachers
        </h1>
        <p
          data-testid="hero-subtitle"
          className="text-lg md:text-xl text-[#475569] max-w-2xl mx-auto font-medium leading-relaxed mb-8"
        >
          Create classroom-ready rubrics by state, subject, grade level, and
          assignment topic — in seconds.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href="#tool"
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white rounded-xl px-6 py-3.5 font-bold text-base hover:bg-[#1E1E1E] transition-colors shadow-sm"
          >
            <Lightning size={20} weight="fill" />
            Generate a Free Rubric
          </a>
          <a
            href="#resources"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 bg-white border border-[#CBD5E1] text-[#0A0A0A] rounded-xl px-6 py-3.5 font-bold text-base hover:bg-[#F8FAFC] transition-colors shadow-sm"
          >
            <Printer size={20} weight="bold" />
            View Printable Teacher Resources
          </a>
        </div>

        <ul className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[#475569] font-medium">
          {["Print or copy in one click", "Works for K-12", "Mobile-friendly"].map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5">
              <CheckCircle size={16} weight="fill" className="text-[#A8B5A2]" />
              {t}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function ToolSection() {
  return (
    <section id="tool" className="py-14 md:py-20 bg-white scroll-mt-20">
      <div className="max-w-4xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10">
          <p className="text-sm font-bold uppercase tracking-widest text-[#64748B] mb-2">
            The Tool
          </p>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3">
            Build your rubric
          </h2>
          <p className="text-base md:text-lg text-[#475569] font-medium max-w-2xl mx-auto">
            Fill in a few details and your printable rubric will appear right below.
          </p>
        </div>
        <RubricGenerator />
      </div>
    </section>
  );
}

function FreeDownloadsSection() {
  return (
    <section id="downloads" className="py-14 md:py-20 bg-[#FAFAF9] border-y border-[#E5E7EB] scroll-mt-20">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10">
          <p className="text-sm font-bold uppercase tracking-widest text-[#64748B] mb-2">
            Free
          </p>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3">
            Free Teacher Downloads
          </h2>
          <p className="text-base md:text-lg text-[#475569] font-medium max-w-2xl mx-auto">
            Grab a printable to use in class today — no email required.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FREE_DOWNLOADS.map((item) => (
            <div
              key={item.id}
              data-testid={`free-${item.id}`}
              className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: item.bg }}
              >
                {item.icon}
              </div>
              <h3 className="font-display font-black text-xl tracking-tight mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-[#475569] font-medium leading-relaxed mb-5 flex-1">
                {item.description}
              </p>
              <button
                type="button"
                data-testid={`download-${item.id}`}
                onClick={() => {
                  const a = document.createElement("a");
                  a.href = item.file;
                  a.setAttribute("download", "");
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }}
                className="inline-flex items-center justify-center gap-2 bg-[#0A0A0A] text-white rounded-xl px-4 py-2.5 font-bold text-sm hover:bg-[#1E1E1E] transition-colors"
              >
                <DownloadSimple size={18} weight="bold" />
                Download Free
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PaidProductsSection() {
  return (
    <section id="resources" className="py-14 md:py-20 bg-white scroll-mt-20">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10">
          <p className="text-sm font-bold uppercase tracking-widest text-[#64748B] mb-2">
            Premium
          </p>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-3">
            Printable Teacher Resources
          </h2>
          <p className="text-base md:text-lg text-[#475569] font-medium max-w-2xl mx-auto">
            Beautifully designed printables for busy classrooms. One-time
            purchase, yours forever.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PAID_PRODUCTS.map((p) => (
            <div
              key={p.id}
              data-testid={`product-${p.id}`}
              className="bg-white rounded-2xl border border-[#E5E7EB] p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col"
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                style={{ backgroundColor: p.bg }}
              >
                {p.icon}
              </div>
              <h3 className="font-display font-black text-lg tracking-tight mb-1.5">
                {p.title}
              </h3>
              <p className="text-sm text-[#475569] font-medium leading-relaxed flex-1 mb-4">
                {p.description}
              </p>
              <div className="flex items-center justify-between gap-3">
                <span className="font-display font-black text-2xl text-[#0A0A0A]">
                  {p.price}
                </span>
                <a
                  href={productUrl(p.id)}
                  data-testid={`view-${p.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-white rounded-xl px-4 py-2 font-bold text-sm hover:bg-[#1E1E1E] transition-colors"
                >
                  <ShoppingBag size={16} weight="bold" />
                  View Product
                </a>
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-[#64748B] font-medium mt-8">
          Product links will open external storefronts (Etsy, Gumroad, or Payhip).
        </p>
      </div>
    </section>
  );
}

function SeoSection() {
  return (
    <section className="py-14 md:py-20 bg-[#FAFAF9] border-t border-[#E5E7EB]">
      <div className="max-w-3xl mx-auto px-5 md:px-8 space-y-10">
        <div>
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-3">
            How This Free Rubric Generator Helps Teachers
          </h2>
          <p className="text-base md:text-lg text-[#1F2937] font-medium leading-relaxed">
            This free rubric generator helps teachers quickly create classroom-ready
            rubrics based on subject, grade level, state, and assignment topic. It
            is designed to save time, support clear grading expectations, and make
            classroom assessment easier.
          </p>
        </div>
        <div>
          <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight mb-3">
            Who Can Use This Tool?
          </h2>
          <p className="text-base md:text-lg text-[#1F2937] font-medium leading-relaxed">
            Teachers, homeschool parents, tutors, and classroom support staff can
            use this tool to create rubrics for writing assignments, projects,
            presentations, science activities, social studies work, math tasks,
            and more.
          </p>
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-14 md:py-20 bg-white border-t border-[#E5E7EB]">
      <div className="max-w-3xl mx-auto px-5 md:px-8">
        <div className="text-center mb-10">
          <p className="text-sm font-bold uppercase tracking-widest text-[#64748B] mb-2">
            FAQ
          </p>
          <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight">
            Frequently asked questions
          </h2>
        </div>
        <div className="space-y-3" data-testid="faq-list">
          {FAQS.map((item, i) => {
            const isOpen = open === i;
            return (
              <div
                key={item.q}
                className="border border-[#E5E7EB] rounded-xl bg-white overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`faq-q-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 hover:bg-[#FAFAF9] transition-colors"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-base text-[#0A0A0A]">
                    {item.q}
                  </span>
                  <CaretDown
                    size={18}
                    weight="bold"
                    className={"transition-transform " + (isOpen ? "rotate-180" : "")}
                  />
                </button>
                {isOpen ? (
                  <div
                    data-testid={`faq-a-${i}`}
                    className="px-5 pb-5 text-[#475569] font-medium text-base leading-relaxed"
                  >
                    {item.a}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <a
            href="#tool"
            className="inline-flex items-center gap-2 font-bold text-[#0A0A0A] hover:underline"
          >
            Try the free rubric generator <ArrowRight size={16} weight="bold" />
          </a>
        </div>
      </div>
    </section>
  );
}
